import * as Sentry from '@sentry/react';
import { CSSProperties } from 'react';
import { UnknownAction } from 'redux';

import { gengql } from '@/__generated__';
import { ERROR_CODES, hasErrorCode } from '@/core/api';
import { UserContext, getUserContextOptions, isGroupContext } from '@/core/user';
import { AppThunk } from '@/store';
import { setCache } from '@/utils';

import ActionTypes from './ActionTypes';

export function notify(message = '', messageType = 'success', duration = 8000): UnknownAction {
  return {
    type: ActionTypes.UI__SHOW_MESSAGE,
    message,
    messageType,
    duration,
  };
}

export function hideMessage(): UnknownAction {
  return {
    type: ActionTypes.UI__HIDE_MESSAGE,
  };
}

interface PopupOptions {
  title: string;
  layout?: string;
  contents?: any;
  buttons?: any;
  buttonAlignment?: any;
  links?: any;
  contentStyle?: CSSProperties;
}

export function popup({
  layout,
  title,
  contents,
  buttons,
  links,
  contentStyle,
  buttonAlignment,
}: PopupOptions): UnknownAction {
  return {
    type: ActionTypes.UI__SHOW_POPUP,
    layout,
    title,
    contents,
    buttons,
    links,
    contentStyle,
    buttonAlignment,
  };
}

export function hidePopup(): UnknownAction {
  return {
    type: ActionTypes.UI__HIDE_POPUP,
  };
}

const GET_GROUP = gengql(/* GraphQL */ `
  query actionSetUserContextGetGroup($id: String!) {
    group(id: $id) {
      id
      name
      billing_account {
        id
      }
    }
  }
`);

export function setUserContext(userContext: UserContext): AppThunk<Promise<void>> {
  return async (dispatch, getState, { graphql }) => {
    if (!userContext) throw new Error('user context cannot be empty');

    const { viewer } = getState();

    let resolvedContext = userContext;
    let group;
    let persist = true;
    if (isGroupContext(userContext)) {
      try {
        const { data } = await graphql.client.query({
          query: GET_GROUP,
          variables: { id: userContext },
        });
        group = data.group;
      } catch (err) {
        // The group(id) query failed. Never let context restore throw — degrade to a
        // query-free personal context (client/admin, never another group, which is what
        // guarantees the thunk can't throw). getDefaultUserContext (Layer 1) already drops
        // stale groups on load, so reaching here is an anomaly worth signal — but how we
        // treat it depends on why:
        //   • A real group:view denial = membership revoked → the downgrade is correct and
        //     permanent, so persist it and report at 'warning'.
        //   • Any other failure (transient/network) → degrade for THIS session only: do NOT
        //     persist, so the next boot re-attempts the group and self-heals; report at
        //     'error' since a sticky downgrade here would be a real regression. (ENG-5509)
        const denied = hasErrorCode(err, ERROR_CODES.PERMISSION_NOT_ALLOWED);
        Sentry.captureException(err, {
          level: denied ? 'warning' : 'error',
          tags: { flow: 'user-context-restore' },
          extra: { attemptedContext: userContext },
        });
        resolvedContext = viewer.admin ? 'admin' : 'client';
        persist = denied;
      }
    }

    const userContextOptions = getUserContextOptions(viewer, resolvedContext, group && [group]);

    if (persist) setCache('user_context', resolvedContext);

    dispatch({
      type: ActionTypes.UI__SET_USER_CONTEXT,
      userContext: resolvedContext,
      userContextOptions,
    });
  };
}

export function saveUserView(userId: string): UnknownAction {
  return {
    type: ActionTypes.UI__SAVE_USER_VIEW,
    userId,
  };
}
